import { Alert, Box, Stack, Typography } from "@mui/material";
import { type FC, useMemo, useState } from "react";
import { SeverityPill } from "../../../modules/generic/components/severity-pill";
import type { RenderPollResults } from "./context/moment-polls-context";
import { PollsAnswers } from "./ui/answers";
import type { DisplayPerson } from "./ui/display-person";
import { PollsOverviewHeader } from "./ui/header";
import { PollsNaughtyList } from "./ui/naughty-list";
import { PollResponders } from "./ui/responders";

export const MomentPoll: FC<RenderPollResults> = ({
  hasResponsed,
  noResponses,
  poll_id,
  question,
  results,
  totalResponses,
  sentTimes,
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);

  const persons: DisplayPerson[] = useMemo(() => {
    // if nothing highlighted then show only the no responses list
    if (selectedAnswers.length === 0) {
      return noResponses;
    }
    // Otherwise, filter for the selected answers and join the results
    // removing duplicate persons. Carry the answer text through so the table
    // can show which answer each person gave.
    return results
      .filter(({ text }) => selectedAnswers.includes(text))
      .flatMap((result) =>
        result.responses.map((response) => ({ ...response, answer: result.text })),
      )
      .filter(
        (person, index, self) =>
          self.findIndex((p) => p.person_id === person.person_id) === index ||
          self.findIndex((p) => p.destination === person.destination) === index,
      );
  }, [selectedAnswers, noResponses, results]);

  return (
    <Box
      key={poll_id}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        padding: 2,
        backgroundColor: "background.paper",
        boxShadow: 1,
      }}
    >
      <Stack direction="column">
        {!hasResponsed && <Alert severity="info">No responses received yet</Alert>}
        <PollsOverviewHeader question={question} sentTimes={sentTimes} poll_id={poll_id} />
        {hasResponsed && (
          <Box>
            <SeverityPill
              sx={{
                width: "auto",
              }}
              color="primary"
            >
              {totalResponses} response{totalResponses === 1 ? "" : "s"}
            </SeverityPill>
          </Box>
        )}
        <PollsAnswers
          results={results}
          selectedAnswers={selectedAnswers}
          setSelectedAnswers={setSelectedAnswers}
        />

        {noResponses.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Everyone has responded to this poll
          </Typography>
        )}

        {selectedAnswers.length > 0 && (
          <PollResponders persons={persons} selectedAnswers={selectedAnswers} />
        )}

        {selectedAnswers.length === 0 && noResponses.length > 0 && (
          // Opnly show the naughty list if there are no responses
          <PollsNaughtyList persons={noResponses} />
        )}
      </Stack>
    </Box>
  );
};
